/**
 * The journal in three buckets — what you are waiting for, what needs
 * attention, and what is working.
 *
 * A plan list sorted by date answers "what did I do". This answers "what should
 * I look at now", which is the question a trading day is actually spent on.
 *
 * ## Its own columns, not the screener's
 *
 * The screener's vocabulary is *symbol* columns — price, change, sector — and
 * none of them say whether a position is working. So this carries the position
 * ones instead: entry, stop, R now, open risk, days held. That is why these
 * three are not a fourth `sym/groups` `GroupType`: served through the screener
 * they would tell you *which* symbols and not *why*, which is most of the value
 * gone. See `lib/trade-groups.ts`.
 *
 * ## It replaces the grid rather than taking a cell in it
 *
 * Not a `Section`, and not a fourth `Section.kind`. What is on screen is view
 * state (D8): a trades pane saved into the layout would be restored on every
 * boot and then argue with the charts over the same space, and it would need a
 * contract change to store something the user never asked to keep.
 *
 * Swapped rather than hidden, because the charts hold WebGL surfaces — leaving
 * them mounted behind this would keep them rendering to nobody.
 *
 * ## Every number is derived
 *
 * Nothing here is stored. R, open risk and the day count all come from the
 * plan, its fills and the live price, exactly as `plan-math` computes them for
 * the chart's own lines — so a corrected fill changes this view and the chart
 * together, and there is no second copy to go stale.
 */

import { useMemo } from "react";
import type { Execution, Plan, Trade } from "@qufolio/model";
import { formatMoney, marketForSymbol } from "../lib/market";

import {
  TRADE_GROUPS,
  executionsByPlan,
  groupPlans,
  type TradeGroup,
} from "../lib/trade-groups";
import {
  netPosition,
  openRisk,
  rMultiple,
  realisedR,
  riskEntry,
  riskStop,
} from "../lib/plan-math";

export interface TradesViewProps {
  plans: readonly Plan[];
  trades: readonly Trade[];
  executions: readonly Execution[];
  /** Last trade per symbol, uppercased. Absent means the chart never loaded it. */
  prices: ReadonlyMap<string, number>;
  active: TradeGroup;
  onPick: (group: TradeGroup) => void;
  /** Chart this plan's symbol. The reason to have the list beside the charts. */
  onSelectSymbol?: (symbol: string) => void;
  onEdit?: (plan: Plan) => void;
}

export function TradesView({
  plans,
  trades,
  executions,
  prices,
  active,
  onPick,
  onSelectSymbol,
  onEdit,
}: TradesViewProps) {
  // Bucketed once for the counts as well as the rows — the counts are on the
  // tabs, so computing them separately would be the same work twice and a
  // chance for a tab to disagree with what it opens.
  const grouped = useMemo(
    () => groupPlans(plans, trades, executions, prices),
    [plans, trades, executions, prices],
  );
  const fills = useMemo(() => executionsByPlan(trades, executions), [trades, executions]);

  const rows = grouped[active];
  const spec = TRADE_GROUPS.find((g) => g.id === active)!;
  // Named for what it holds, because on a finished trade it is what was made
  // rather than what it is worth — and a column called "R" in both places would
  // hide the difference.
  const rHeader = active === "completed" ? "Realised R" : "R";

  return (
    <div style={HOST}>
      <div style={TABS} role="tablist" aria-label="Trade groups">
        {TRADE_GROUPS.map((group) => (
          <button
            key={group.id}
            type="button"
            role="tab"
            aria-selected={group.id === active}
            title={group.hint}
            onClick={() => onPick(group.id)}
            style={{ ...TAB, ...(group.id === active ? TAB_ON : null) }}
          >
            {group.label}
            {/* The count is on the tab because the whole point of three buckets
                is that they partition — a tab with no number cannot be read as
                part of a total. */}
            <span style={COUNT}>{grouped[group.id].length}</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        // An empty state that says what would put something here, rather than
        // "no results" — with a new journal all three are empty at once and a
        // blank panel reads as broken.
        <div style={EMPTY}>{spec.hint}</div>
      ) : (
        <div style={{ overflow: "auto" }}>
          <table style={TABLE}>
            <thead>
              <tr>
                {["Symbol", "Dir", "Entry", "Stop", "Now", rHeader, "Open risk", "Days", ""].map(
                  (h) => (
                    <th key={h} style={TH}>
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((plan) => (
                <Row
                  key={plan.id}
                  plan={plan}
                  executions={fills.get(plan.id) ?? []}
                  price={prices.get(plan.symbol.toUpperCase())}
                  onSelectSymbol={onSelectSymbol}
                  onEdit={onEdit}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({
  plan,
  executions,
  price,
  onSelectSymbol,
  onEdit,
}: {
  plan: Plan;
  executions: readonly Execution[];
  price: number | undefined;
  onSelectSymbol?: (symbol: string) => void;
  onEdit?: (plan: Plan) => void;
}) {
  const { quantity } = netPosition(executions, plan.direction);
  const open = Math.abs(quantity) > 0;
  const traded = executions.length > 0;
  // The **frozen** entry and stop once there is one, so the row measures against
  // what the plan was when it filled rather than what it has been amended to.
  const entry = riskEntry(plan);
  const stop = riskStop(plan);

  // **R means two different things and the column must not conflate them.**
  // While a position is open it is a mark to market and moves with the price;
  // once it is out it is what the trade actually made, and the live price is
  // irrelevant — a closed trade showing R against today's quote would keep
  // "improving" for weeks after it ended.
  const r = open
    ? price !== undefined && Number.isFinite(price)
      ? rMultiple(plan, price)
      : undefined
    : traded
      ? realisedR(plan, executions)
      : undefined;

  // Days *held*, so a closed trade measures first fill to last rather than to
  // now — otherwise every finished trade would age forever in the list.
  const held = traded
    ? Math.floor(
        ((open ? Date.now() : Date.parse(executions[executions.length - 1]!.executedAt)) -
          Date.parse(executions[0]!.executedAt)) /
          86_400_000,
      )
    : undefined;

  return (
    <tr style={TR}>
      <td style={TD}>
        <button type="button" onClick={() => onSelectSymbol?.(plan.symbol)} style={LINK}>
          {plan.symbol}
        </button>
      </td>
      <td style={TD}>{plan.direction === "short" ? "S" : "L"}</td>
      <td style={NUM}>{entry.toFixed(2)}</td>
      <td style={NUM}>{stop.toFixed(2)}</td>
      <td style={NUM}>{price === undefined ? "—" : price.toFixed(2)}</td>
      <td
        style={{
          ...NUM,
          // Only R is coloured. Colouring every number makes a row a rainbow and
          // R is the one that says whether this is going well.
          color:
            r === undefined
              ? "var(--qf-text-muted)"
              : r > 0
                ? "var(--qf-positive, #30a46c)"
                : r < 0
                  ? "var(--qf-negative, #e5484d)"
                  : "inherit",
        }}
      >
        {r === undefined ? "—" : `${r >= 0 ? "+" : ""}${r.toFixed(2)}R`}
      </td>
      {/* What is still on the table if the stop is hit from here — zero once the
          position is out, which is the number the time limit is about. */}
      <td style={NUM}>{open
          ? formatMoney(openRisk(plan, executions), marketForSymbol(plan.symbol), { decimals: 0 })
          : "—"}</td>
      <td style={NUM}>{held === undefined ? "—" : held}</td>
      <td style={TD}>
        {onEdit && (
          <button type="button" onClick={() => onEdit(plan)} style={LINK}>
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

const HOST: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TABS: React.CSSProperties = {
  display: "flex",
  gap: 2,
  padding: "4px 6px",
  borderBottom: "1px solid var(--qf-border)",
};

const TAB: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 10px",
  background: "transparent",
  border: "1px solid transparent",
  borderRadius: 4,
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  font: "inherit",
};

const TAB_ON: React.CSSProperties = {
  background: "var(--qf-surface-raised)",
  borderColor: "var(--qf-border)",
  color: "var(--qf-text)",
};

const COUNT: React.CSSProperties = {
  padding: "0 5px",
  borderRadius: 8,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text-muted)",
  fontSize: 11,
};

const TABLE: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const TH: React.CSSProperties = {
  padding: "4px 8px",
  textAlign: "left",
  color: "var(--qf-text-muted)",
  fontWeight: 500,
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const TR: React.CSSProperties = { borderBottom: "1px solid var(--qf-border)" };
const TD: React.CSSProperties = { padding: "3px 8px", whiteSpace: "nowrap" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const LINK: React.CSSProperties = {
  padding: 0,
  background: "transparent",
  border: "none",
  color: "var(--qf-accent)",
  cursor: "pointer",
  font: "inherit",
};
const EMPTY: React.CSSProperties = { padding: 16, color: "var(--qf-text-muted)" };
